import { useMemo } from 'react'
import { Loader2, MessageSquarePlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { type LlmChatSessionDto } from '@/shared/hooks/api/llmChat'
import { useListProducts } from '@/shared/hooks/api/product'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import { llmChatSessionListLabel } from '../llmChatSessionLabels'
import {
  buildProductDisplayNameMap,
  getLlmChatSessionScopeDisplay,
} from '../llmChatSessionScopeLabel'
import { LlmChatSessionScopeBadge } from './LlmChatSessionScopeBadge'

type LlmChatSidebarProps = {
  sessions: LlmChatSessionDto[]
  selectedSessionId: string | undefined
  isLoading: boolean
  onSelectSession: (sessionId: string) => void
  onNewChat: () => void
}

export function LlmChatSidebar({
  sessions,
  selectedSessionId,
  isLoading,
  onSelectSession,
  onNewChat,
}: LlmChatSidebarProps) {
  const { t } = useTranslation('llmChat')
  const productsQuery = useListProducts()
  const noNameProductLabel = t('newChatWizard.productSelect.noName')

  const productDisplayNameMap = useMemo(
    () =>
      buildProductDisplayNameMap(productsQuery.data ?? [], noNameProductLabel),
    [noNameProductLabel, productsQuery.data]
  )

  return (
    <aside
      className={cn(
        'relative flex max-h-[min(40vh,22rem)] min-h-0 w-full max-w-full min-w-0 shrink-0 flex-col overflow-hidden border-b border-border/60 bg-muted/10 md:h-full md:max-h-none md:w-[260px] md:max-w-[260px] md:border-r md:border-b-0 dark:bg-muted/5'
      )}
    >
      <div className='shrink-0 space-y-2 border-b border-border p-2.5'>
        <p className='px-2 text-xs font-medium tracking-wide text-muted-foreground uppercase'>
          {t('conversations')}
        </p>
        <Button
          type='button'
          className='h-8 w-full justify-start gap-2 text-sm font-normal'
          variant='outline'
          size='sm'
          onClick={onNewChat}
        >
          <MessageSquarePlus className='size-3.5 shrink-0 opacity-70' />
          {t('newChat')}
        </Button>
      </div>

      <div className='min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto'>
        <div className='flex w-full min-w-0 flex-col gap-px p-1.5 pb-3'>
          {isLoading && (
            <div className='flex justify-center py-6'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          )}
          {!isLoading && sessions.length === 0 && (
            <p className='px-2 py-3 text-center text-xs text-muted-foreground'>
              {t('noChats')}
            </p>
          )}
          {sessions.map((session) => {
            const { primary, secondary } = llmChatSessionListLabel(
              session,
              t('untitled')
            )
            const scope = getLlmChatSessionScopeDisplay({
              productId: session.productId,
              productDisplayNameMap,
              productsLoaded: productsQuery.isSuccess,
              translate: t,
            })
            const isActive = session.id === selectedSessionId
            return (
              <button
                key={session.id}
                type='button'
                onClick={() => {
                  onSelectSession(session.id)
                }}
                className={cn(
                  'flex w-full min-w-0 max-w-full flex-col items-start gap-1 overflow-hidden rounded-md p-2 text-left transition-colors',
                  isActive
                    ? 'border-primary bg-muted/50 text-foreground dark:bg-muted/30'
                    : 'border-transparent hover:bg-muted/40'
                )}
              >
                <span className='line-clamp-2 w-full min-w-0 text-sm leading-snug font-normal'>
                  {primary}
                </span>
                {scope !== null ? (
                  <LlmChatSessionScopeBadge
                    label={scope.label}
                    scopeKind={scope.scopeKind}
                  />
                ) : null}
                <span className='min-w-0 truncate text-xs text-muted-foreground'>
                  {secondary}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </aside>
  )
}
